import { Filter, RotateCcw } from 'lucide-react'

export interface CarFilterState {
  brand: string
  model: string
  year: string
  priceMax: string
  fuel: string
  transmission: string
  bodyType: string
  ownership: string
  verifiedOnly: boolean
}

interface FilterSidebarProps {
  filters: CarFilterState
  onFilterChange: (filters: CarFilterState) => void
  onReset: () => void
}

export function FilterSidebar({ filters, onFilterChange, onReset }: FilterSidebarProps) {
  const handleChange = (key: keyof CarFilterState, value: any) => {
    onFilterChange({ ...filters, [key]: value })
  }

  return (
    <aside className="w-full space-y-6 rounded-2xl border border-border bg-card p-6 shadow-xs">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div className="flex items-center gap-2 font-display text-lg font-bold text-foreground">
          <Filter className="h-5 w-5 text-primary" /> Filters
        </div>
        <button
          onClick={onReset}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Reset
        </button>
      </div>

      {/* Brand Select */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Make / Brand</label>
        <select
          value={filters.brand}
          onChange={(e) => handleChange('brand', e.target.value)}
          className="w-full h-10 rounded-xl bg-background border border-border px-3 text-xs text-foreground focus:border-primary focus:outline-none"
        >
          <option value="" className="bg-popover text-popover-foreground">All Brands</option>
          <option value="Apex" className="bg-popover text-popover-foreground">Apex Motors</option>
          <option value="Monarch" className="bg-popover text-popover-foreground">Monarch GT</option>
          <option value="Veritas" className="bg-popover text-popover-foreground">Veritas Cars</option>
          <option value="Aura" className="bg-popover text-popover-foreground">Aura Luxury</option>
        </select>
      </div>

      {/* Body Type */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Body Type</label>
        <select
          value={filters.bodyType}
          onChange={(e) => handleChange('bodyType', e.target.value)}
          className="w-full h-10 rounded-xl bg-background border border-border px-3 text-xs text-foreground focus:border-primary focus:outline-none"
        >
          <option value="" className="bg-popover text-popover-foreground">All Body Types</option>
          <option value="Coupe" className="bg-popover text-popover-foreground">Coupe</option>
          <option value="Sedan" className="bg-popover text-popover-foreground">Sedan</option>
          <option value="SUV" className="bg-popover text-popover-foreground">SUV</option>
          <option value="Convertible" className="bg-popover text-popover-foreground">Convertible</option>
        </select>
      </div>

      {/* Fuel Type */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fuel Type</label>
        <select
          value={filters.fuel}
          onChange={(e) => handleChange('fuel', e.target.value)}
          className="w-full h-10 rounded-xl bg-background border border-border px-3 text-xs text-foreground focus:border-primary focus:outline-none"
        >
          <option value="" className="bg-popover text-popover-foreground">All Fuels</option>
          <option value="V8 Hybrid" className="bg-popover text-popover-foreground">V8 Hybrid</option>
          <option value="Twin Turbo V6" className="bg-popover text-popover-foreground">Twin Turbo V6</option>
          <option value="All-Electric" className="bg-popover text-popover-foreground">All-Electric</option>
          <option value="Naturally Aspirated V12" className="bg-popover text-popover-foreground">Naturally Aspirated V12</option>
        </select>
      </div>

      {/* Transmission */}
      <div className="space-y-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Transmission</label>
        <select
          value={filters.transmission}
          onChange={(e) => handleChange('transmission', e.target.value)}
          className="w-full h-10 rounded-xl bg-background border border-border px-3 text-xs text-foreground focus:border-primary focus:outline-none"
        >
          <option value="" className="bg-popover text-popover-foreground">All Transmissions</option>
          <option value="8-Speed DCT" className="bg-popover text-popover-foreground">8-Speed DCT</option>
          <option value="Automatic" className="bg-popover text-popover-foreground">Automatic</option>
          <option value="Single Speed" className="bg-popover text-popover-foreground">Single Speed</option>
          <option value="7-Speed Sequential" className="bg-popover text-popover-foreground">7-Speed Sequential</option>
        </select>
      </div>

      {/* Checkbox Verified */}
      <div className="pt-2 border-t border-border">
        <label className="flex items-center gap-3 text-xs font-medium text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={filters.verifiedOnly}
            onChange={(e) => handleChange('verifiedOnly', e.target.checked)}
            className="h-4 w-4 rounded border-border bg-background text-primary focus:ring-primary"
          />
          <span>Verified Dealers &amp; Sellers Only</span>
        </label>
      </div>
    </aside>
  )
}
